import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { smartGovernanceOff, smartGovernanceOn } from 'kosmos/images';
import { CompanyScoringState, OrganisationAssignmentDto } from 'models/dtoTypes';
import { ESmartGovernanceStatus } from '../SmartGovernanceWidget';
import { SmartGovernanceAssignmentAttributes } from 'kosmos/models';
import CreateResourceModal, { ISchema } from 'kosmos/dialogs/CreateResourceModal';
import { get } from 'lodash';
import { CreateOrganisationAssignment } from 'services/organisationAssignment';
import axios from 'axios';
import { CurrentUser } from 'auth';
import { EFeature } from 'auth/Authentication';
import { useTranslation } from 'react-i18next';
import AlertDialog from 'kosmos/dialogs/AlertDialog';
import React from 'react';

const PREFIX = '_smartGovernanceVerificationLogo';

const classes = {
  content: `${PREFIX}-content`,

  column: `${PREFIX}-column`,
  icon: `${PREFIX}-icon`,
};

const Root = styled('div')(() => ({
  float: 'right',
  [`& .${classes.content}`]: {
    position: 'relative',
    width: 235,
    display: 'flex',
    cursor: 'pointer',
    [`& .${classes.column}`]: {
      flex: 1,
      paddingLeft: 3,
      paddingRight: 3,
    },
    [`& .${classes.icon}`]: {
      border: '1px solid #3543d0',
      borderRadius: 4,
      width: '100%',
      padding: 3,
    },
    [`&.inactive .${classes.icon}`]: {
      border: '1px solid #949494',
      borderRadius: 4,
      width: '100%',
      padding: 3,
    },
    ['&.inactive h6']: {
      color: '#949494',
    },
  },
}));

interface IProps {
  kosmosCompanyId: string;
  smartGovernanceAssignment?: OrganisationAssignmentDto;
  onClickCompleted?: (id: string) => void;
  align: 'left' | 'right';
}

export default function SmartGovernanceVerificationLogo(props: IProps): JSX.Element {
  const { align, smartGovernanceAssignment, kosmosCompanyId } = props;
  // only user can assign sg can have onClick function
  const disabled = !CurrentUser.getInstance().canManageResource(EFeature.assignSg);
  // once sent to sme, it should be active
  const active = Boolean(smartGovernanceAssignment?.manualCompanyId);
  const [openCreate, setOpenCreate] = useState(false);
  const [smartGovernanceStatus, setSmartGovernanceStatus] = useState<ESmartGovernanceStatus>();
  const { t } = useTranslation('default');
  const [open, setOpen] = React.useState<boolean>(false);
  const [alert, setAlert] = React.useState<{
    header: string;
    message: string;
    button: string;
  }>({ header: '', message: '', button: '' });

  const renderIcon = () => {
    const src = active ? smartGovernanceOn : smartGovernanceOff;
    return <img src={src} className={classes.icon} />;
  };

  useEffect(() => {
    setSmartGovernanceStatus(getSmartGovernanceStatus());
  }, [smartGovernanceAssignment]);

  const getSmartGovernanceStatus = (): ESmartGovernanceStatus => {
    if (!smartGovernanceAssignment) {
      return ESmartGovernanceStatus.notStarted;
    } else if (!smartGovernanceAssignment.isAccepted) {
      return ESmartGovernanceStatus.sent;
    } else if (!smartGovernanceAssignment.manualCompanyId) {
      return ESmartGovernanceStatus.assignError;
    } else {
      const scoringState = smartGovernanceAssignment?.manualCompany?.scoringState;
      if (scoringState === CompanyScoringState.Completed) {
        return ESmartGovernanceStatus.completed;
      } else if (scoringState === CompanyScoringState.InProgress) {
        return ESmartGovernanceStatus.inProgress;
      } else if (scoringState === CompanyScoringState.Error) {
        return ESmartGovernanceStatus.scoreError;
      }
      return ESmartGovernanceStatus.accepted;
    }
  };

  const handleSelectSgCompany = () => {
    if (disabled) {
      return;
    }
    const status = smartGovernanceStatus;
    if (status === ESmartGovernanceStatus.notStarted) {
      setOpenCreate(true);
      return;
    } else if (status === ESmartGovernanceStatus.completed) {
      window.open(`/combined-reports/${kosmosCompanyId}`, '_blank');
      return;
    }
  };

  const renderText = () => {
    const style = {
      marginTop: 5,
      textAlign: align,
    };

    return (
      <div style={style}>
        <Typography variant="h6" color="secondary" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
          SMART
        </Typography>
        <Typography variant="h6" color="secondary" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
          GOVERNANCE
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            lineHeight: 1.1,
            textTransform: 'uppercase',
          }}
        >
          {smartGovernanceStatus}
        </Typography>
      </div>
    );
  };

  const schema: ISchema = {
    fields: [
      {
        label: t('assign-to'),
        name: 'assigneeId',
        required: true,
        widget: 'smeUserSelector',
        description: '',
      },
    ],
  };

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      const errorMessage =
        get(e.response?.data, 'message') || t('smart-governance-assignment-has-failed');
      setAlert({ header: t('not-assigned'), message: errorMessage, button: t('ok') });
      setOpen(true);
    } else {
      // native error
      console.error(e);
      const errorMessage = e instanceof Error ? e.message : String(e);
      setAlert({ header: t('not-assigned'), message: errorMessage, button: t('ok') });
      setOpen(true);
    }
  };

  const handleCreate = async (attrs: SmartGovernanceAssignmentAttributes) => {
    console.log(attrs);
    try {
      await CreateOrganisationAssignment({
        kosmosCompanyId: kosmosCompanyId,
        assigneeId: get(attrs, 'assigneeId') || '',
      });
      setSmartGovernanceStatus(ESmartGovernanceStatus.sent);
      setOpenCreate(false);
      setAlert({
        header: t('assigned'),
        message: t('smart-governance-has-been-assigned-successfully'),
        button: t('ok'),
      });
      setOpen(true);
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <Root>
      <div
        className={`${classes.content} ${active ? 'active' : 'inactive'}`}
        onClick={handleSelectSgCompany}
      >
        {(align === 'right' && (
          <>
            <div className={classes.column}>{renderText()}</div>
            <div className={classes.column}>{renderIcon()}</div>
          </>
        )) || (
          <>
            <div className={classes.column}>{renderIcon()}</div>
            <div className={classes.column}>{renderText()}</div>
          </>
        )}
      </div>
      <CreateResourceModal<SmartGovernanceAssignmentAttributes>
        open={openCreate}
        label={t('assign-smart-governance')}
        schema={schema}
        onClose={() => setOpenCreate(false)}
        onSave={handleCreate}
      />
      <AlertDialog
        header={alert.header}
        message={alert.message}
        button={alert.button}
        onClose={() => setOpen(false)}
        isOpen={open}
      ></AlertDialog>
    </Root>
  );
}
